import { useState, useEffect, useMemo } from 'react';
import { Plus, ChevronLeft, ChevronRight, Settings2, UserPlus } from 'lucide-react';
import { palette, shadow, transition, admin } from '../../styles/tokens';
import { useAuthStore } from '../../stores/authStore';
import { useIndustryLens } from '../../hooks/useIndustryLens';
import { useBookings, useAppointmentGrid } from './hooks';
import { AppointmentGrid } from './AppointmentGrid';
import { StatsPanel } from './StatsPanel';
import { CreateBookingPanel } from './CreateBookingPanel';
import { BookingDetailPanel } from './BookingDetailPanel';
import { ChairManagementModal } from './ChairManagementModal';
import { UpcomingTab } from './UpcomingTab';
import { PastTab } from './PastTab';
import { BookingHistoryList } from './BookingHistoryList';
import { Skeleton, TableSkeleton } from '../../components/common/Skeleton';
import { Toast } from '../../components/common/Toast';
import { BOOKING_TABS, type BookingTab } from './config';
import type { BookingRow } from './hooks';
import type { BookingSource } from '../../data/enums';

function toDateStr(d: Date): string { return d.toISOString().split('T')[0]; }
function fmtDateLabel(d: string): string {
  const today = toDateStr(new Date());
  const tomorrow = toDateStr(new Date(Date.now() + 86400000));
  if (d === today) return 'Today';
  if (d === tomorrow) return 'Tomorrow';
  return new Date(d + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

export function BookingsPage() {
  const business = useAuthStore(s => s.business);
  const user = useAuthStore(s => s.user);
  const [date, setDate] = useState(toDateStr(new Date()));
  const [selected, setSelected] = useState<BookingRow | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showChairs, setShowChairs] = useState(false);
  const [createDefaults, setCreateDefaults] = useState<Record<string, string>>({});
  const [listTab, setListTab] = useState<BookingTab>('upcoming');
  const [showList, setShowList] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  const { bookings, services, users, loading, summary, createBooking, updateStatus, recordPayment, closeBookingWithoutPayment, refresh } = useBookings(business?.id);
  const { resources, businessHours, refresh: refreshGrid } = useAppointmentGrid(business?.id, date);
  const { features, isLensActive, industryName, clusterKey } = useIndustryLens();
  const isAppointmentCluster = clusterKey === 'A' || clusterKey === 'F';

  useEffect(() => { document.title = isAppointmentCluster ? 'CrewBit · Booking History' : 'CrewBit · Appointments'; }, [isAppointmentCluster]);

  const dayBookings = useMemo(() => bookings.filter(b => b.date === date && b.status !== 'cancelled'), [bookings, date]);

  const handleSlotClick = (operatorId: string, time: string) => {
    setCreateDefaults({ assigned: operatorId, startTime: time, date });
    setShowCreate(true);
  };

  const handleWalkIn = () => {
    const now = new Date();
    const time = `${String(now.getHours()).padStart(2, '0')}:${String(Math.ceil(now.getMinutes() / 15) * 15 % 60).padStart(2, '0')}`;
    setCreateDefaults({ startTime: time, date, source: 'walk_in' satisfies BookingSource });
    setShowCreate(true);
  };

  const handleAdvance = async (id: string, status: string) => {
    if (!user) return;
    const ok = await updateStatus(id, status, user.id);
    if (ok) setToast({ msg: `Booking ${status.replace('_', ' ')}`, type: 'success' });
  };

  const handlePayment = async (id: string, method: string, amount: number, tip: number) => {
    if (!user) return false;
    const ok = await recordPayment(id, method, amount, tip, user.id);
    if (ok) setToast({ msg: 'Payment recorded', type: 'success' });
    return ok;
  };

  const handleCloseUnpaid = async (id: string) => {
    if (!user) return false;
    const ok = await closeBookingWithoutPayment(id, user.id);
    if (ok) setToast({ msg: 'Booking closed without payment', type: 'success' });
    return ok;
  };

  if (isLensActive && !features.showBookings) {
    return (
      <div>
        <div style={{ fontSize: 24, fontWeight: 700, color: admin.header, letterSpacing: '-0.5px', marginBottom: 24 }}>Appointments</div>
        <div style={{ padding: '60px 20px', textAlign: 'center', background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}` }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: palette.g700, marginBottom: 6 }}>Appointments is not used by {industryName} businesses</div>
          <div style={{ fontSize: 13.5, color: palette.g500 }}>Switch your industry lens or exit the lens to see this module.</div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div>
        <Skeleton width={160} height={24} style={{ marginBottom: 24 }} />
        <div style={{ background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}`, padding: 20 }}><TableSkeleton rows={8} columns={4} /></div>
      </div>
    );
  }

  // Appointment clusters: show booking history list (the grid is on /schedule now)
  if (isAppointmentCluster) {
    return (
      <div>
        <BookingHistoryList bookings={bookings} summary={summary} onSelect={setSelected} />
        <BookingDetailPanel booking={selected} showPhotos={['beauty', 'wellness', 'health', 'education', 'automotive'].includes(business?.industry ?? '')} onClose={() => setSelected(null)} onAdvance={handleAdvance} onRecordPayment={handlePayment} onCloseWithoutPayment={handleCloseUnpaid} />
        {toast && <Toast message={toast.msg} type={toast.type} isVisible={!!toast} onDismiss={() => setToast(null)} />}
      </div>
    );
  }

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, color: admin.header, letterSpacing: '-0.5px' }}>Appointments</div>
          <div style={{ fontSize: 13.5, color: admin.caption, marginTop: 2 }}>{dayBookings.length} appointment{dayBookings.length !== 1 ? 's' : ''} · {fmtDateLabel(date)}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={handleWalkIn} style={btnStyle(palette.amber, palette.amberWash)}><UserPlus size={14} /> Walk-in</button>
          <button onClick={() => { setCreateDefaults({ date }); setShowCreate(true); }} style={btnStyle(palette.accent, palette.white, true)}><Plus size={14} /> Book Appointment</button>
          <button onClick={() => setShowChairs(true)} style={btnStyle(palette.g600, 'transparent')}><Settings2 size={14} /> Chairs</button>
        </div>
      </div>

      {/* ── Date Selector ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <button onClick={() => setDate(toDateStr(new Date(new Date(date + 'T12:00:00').getTime() - 86400000)))} style={navBtn}><ChevronLeft size={16} /></button>
        {[0, 1, 2].map(offset => {
          // eslint-disable-next-line react-hooks/purity
          const d = toDateStr(new Date(Date.now() + offset * 86400000));
          const isActive = d === date;
          return (
            <button key={d} onClick={() => setDate(d)} style={{
              padding: '6px 14px', borderRadius: 6, border: isActive ? `1.5px solid ${palette.accent}` : `1px solid ${admin.border}`,
              background: isActive ? palette.accentWash : palette.white, color: isActive ? palette.accentDeep : palette.g700,
              fontSize: 12.5, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', transition: `all ${transition.fast}`,
            }}>{fmtDateLabel(d)}</button>
          );
        })}
        <button onClick={() => setDate(toDateStr(new Date(new Date(date + 'T12:00:00').getTime() + 86400000)))} style={navBtn}><ChevronRight size={16} /></button>
      </div>

      {/* ── Main Content: Stats + Grid ── */}
      <div style={{ display: 'flex', background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}`, boxShadow: shadow.card, overflow: 'hidden', marginBottom: 20 }}>
        <StatsPanel bookings={bookings} resources={resources} businessHours={businessHours} date={date} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <AppointmentGrid
            bookings={bookings}
            resources={resources}
            businessHours={businessHours}
            date={date}
            onSlotClick={handleSlotClick}
            onBookingClick={setSelected}
          />
        </div>
      </div>

      {/* ── List Toggle ── */}
      <button onClick={() => setShowList(!showList)} style={{
        width: '100%', padding: '10px 16px', borderRadius: 8, border: `1px solid ${admin.border}`,
        background: palette.white, cursor: 'pointer', fontFamily: 'inherit',
        fontSize: 13, fontWeight: 500, color: palette.g600, textAlign: 'left',
        display: 'flex', justifyContent: 'space-between',
      }}>
        <span>Upcoming &amp; Past Bookings</span>
        <span style={{ fontSize: 11, color: palette.g400 }}>{showList ? 'Hide' : 'Show'}</span>
      </button>

      {showList && (
        <div style={{ background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}`, padding: 20, boxShadow: shadow.card, marginTop: 8 }}>
          <div style={{ display: 'flex', gap: 0, background: palette.g100, borderRadius: 8, padding: 2, marginBottom: 16, width: 'fit-content' }}>
            {BOOKING_TABS.map(t => (
              <button key={t.key} onClick={() => setListTab(t.key)} style={{
                padding: '6px 16px', borderRadius: 5, border: 'none', cursor: 'pointer',
                fontSize: 12.5, fontWeight: 500, fontFamily: 'inherit',
                background: listTab === t.key ? palette.g800 : 'transparent',
                color: listTab === t.key ? palette.white : palette.g500,
                transition: `all ${transition.fast}`,
              }}>{t.label}</button>
            ))}
          </div>
          {listTab === 'upcoming' && <UpcomingTab bookings={bookings} services={services} users={users} summary={summary} onSelect={setSelected} onAdvance={handleAdvance} onNewBooking={() => setShowCreate(true)} />}
          {listTab === 'past' && <PastTab bookings={bookings} onSelect={setSelected} />}
        </div>
      )}

      {/* ── Panels / Modals ── */}
      <CreateBookingPanel isOpen={showCreate} onClose={() => { setShowCreate(false); setCreateDefaults({}); }}
        onCreate={async (data, uid) => { const ok = await createBooking(data as Record<string, unknown>, uid); if (ok) setToast({ msg: 'Booking created', type: 'success' }); return ok; }}
        services={services} users={users} userId={user?.id ?? ''}
        initialDate={createDefaults.date} initialTime={createDefaults.startTime}
        initialAssigned={createDefaults.assigned} initialSource={createDefaults.source}
      />
      <BookingDetailPanel booking={selected} showPhotos={['beauty', 'wellness', 'health', 'education', 'automotive'].includes(business?.industry ?? '')} onClose={() => setSelected(null)} onAdvance={(id, status) => { void handleAdvance(id, status); }} onRecordPayment={handlePayment} onCloseWithoutPayment={handleCloseUnpaid} />
      {/* eslint-disable-next-line @typescript-eslint/no-floating-promises */}
      <ChairManagementModal isOpen={showChairs} onClose={() => setShowChairs(false)} onChanged={() => { refreshGrid(); refresh(); }} />
      {toast && <Toast message={toast.msg} type={toast.type} isVisible={!!toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}

// ── Button helpers ──
const btnStyle = (color: string, bg: string, primary = false): React.CSSProperties => ({
  display: 'flex', alignItems: 'center', gap: 4, padding: '7px 14px', borderRadius: 8,
  border: primary ? 'none' : `1px solid ${color}20`, background: primary ? color : bg,
  fontSize: 13, fontWeight: 600, color: primary ? palette.white : color,
  cursor: 'pointer', fontFamily: 'inherit', transition: `all 150ms ease`,
});

const navBtn: React.CSSProperties = {
  width: 32, height: 32, borderRadius: 6, border: `1px solid ${admin.border}`,
  background: palette.white, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
};
